import { useLayoutEffect, useMemo, useState } from "react";
import { useThemeSlice } from "../../../components/ui/StatusBadge.jsx";
import { UnderlineTabs } from "../../../components/ui/UnderlineTabs.jsx";
import { Ico } from "../../../components/icons/icons.jsx";
import { DeleteConfirmModal } from "../../../components/modal/DeleteConfirmModal.jsx";
import { useDeleteConfirm } from "../../../hooks/useDeleteConfirm.js";
import { buildAccountDetailTokens, detailActionBtnStyle } from "../../Accounts/accountDetailTheme.js";
import { buildDeviceDetailFields, buildDeviceDetailRows, buildDeviceSummaryFields } from "./deviceUtils.js";
import { DevicePropertiesSection } from "./DevicePropertiesSection.jsx";
import { DeviceDebugPanel } from "./debug/DeviceDebugPanel.jsx";
import { DeviceBindingHistorySection } from "./DeviceBindingHistorySection.jsx";

const ICON_MAP = {
  model: Ico.model,
  sim: Ico.sim,
  device: Ico.device,
  building: Ico.building,
  grid: Ico.grid,
  layers: Ico.layers,
};

const SUB_TABS = [
  { id: "info", label: "Device info" },
  { id: "history", label: "History" },
  { id: "debug", label: "Debug" },
];

export function DeviceDetailPanel({ device, deviceType, sim, accountName, onEdit, onDelete, onAssign, onDeassign }) {
  const { c, dark, accentHex: A } = useThemeSlice();
  const ap = useMemo(() => buildAccountDetailTokens(dark, c, A), [dark, c, A]);
  const [subTab, setSubTab] = useState("info");
  const { requestDelete, deleteModalProps } = useDeleteConfirm();

  useLayoutEffect(() => {
    setSubTab("info");
  }, [device?.id]);

  if (!device) {
    return (
      <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", color: c.sub, fontSize: 13, background: c.bg }}>
        Select a device to view details
      </div>
    );
  }

  const summaryFields = buildDeviceSummaryFields(device);
  const detailFields = buildDeviceDetailFields(device, accountName, deviceType, sim);
  const detailRows = buildDeviceDetailRows(detailFields);
  const isAssigned = Boolean(device.accountId);

  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0, minHeight: 0, background: c.bg }}>
      <UnderlineTabs tabs={SUB_TABS} activeTab={subTab} onChange={setSubTab} />

      {subTab === "info" && (
        <div style={{ flex: 1, overflow: "auto", padding: "14px 16px 16px", minHeight: 0 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 16,
              flexWrap: "wrap",
              padding: "12px 16px",
              borderRadius: 10,
              background: c.hover,
              border: `1px solid ${c.cardBdr}`,
              marginBottom: 12,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: "4px 0", flex: 1, minWidth: 0 }}>
              {summaryFields.map(({ label, value }, idx) => (
                <div key={label} style={{ display: "flex", alignItems: "center" }}>
                  {idx > 0 && (
                    <span aria-hidden style={{ width: 1, height: 28, background: c.cardBdr, margin: "0 20px", flexShrink: 0 }} />
                  )}
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: c.sub, marginBottom: 3 }}>{label}</div>
                    <div
                      style={{
                        fontSize: 12.5,
                        fontWeight: 700,
                        color: c.text,
                        fontFamily: label === "Device ID" ? "ui-monospace, SFMono-Regular, Menlo, monospace" : "inherit",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {value}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
              {!isAssigned && onAssign && (
                <button type="button" onClick={() => onAssign(device)} style={assignBtnStyle(A)}>
                  {Ico.building}
                  Assign to account
                </button>
              )}
              {isAssigned && onDeassign && (
                <button type="button" onClick={() => onDeassign(device)} style={deassignBtnStyle()}>
                  <UnlinkIcon />
                  Deassign
                </button>
              )}
              <button type="button" onClick={() => onEdit(device)} style={detailActionBtnStyle(ap)} aria-label="Edit device">
                {Ico.pencil}
              </button>
              <button
                type="button"
                onClick={() => requestDelete({
                  title: "Delete device",
                  description: isAssigned
                    ? "This device will be permanently removed from the account."
                    : "This device will be permanently removed from inventory.",
                  confirmText: device.deviceId,
                  entityLabel: "Device ID",
                  confirmLabel: "Delete device",
                  onConfirm: () => onDelete(device.id),
                })}
                style={detailActionBtnStyle(ap, "danger")}
                aria-label="Delete device"
              >
                {Ico.trash}
              </button>
            </div>
          </div>

          <div style={{ background: ap.card, border: `1px solid ${ap.cardBorder}`, borderRadius: 10, overflow: "hidden", marginBottom: 12 }}>
            {detailRows.map((row, rowIdx) => (
              <div
                key={rowIdx}
                style={{
                  display: "grid",
                  gridTemplateColumns: row.length === 3 ? "repeat(3, minmax(0, 1fr))" : "minmax(0, 1fr)",
                  borderTop: rowIdx > 0 ? `1px solid ${ap.cardBorder}` : undefined,
                }}
              >
                {row.map((field, colIdx) => (
                  <DeviceFieldCell
                    key={field.label}
                    field={field}
                    c={c}
                    ap={ap}
                    showLeftBorder={colIdx > 0}
                  />
                ))}
              </div>
            ))}
          </div>

          <DevicePropertiesSection device={device} />
        </div>
      )}

      {subTab === "history" && (
        <div style={{ flex: 1, overflow: "auto", padding: "14px 16px 16px", minHeight: 0 }}>
          <DeviceBindingHistorySection device={device} />
        </div>
      )}

      {subTab === "debug" && (
        <DeviceDebugPanel device={device} />
      )}

      <DeleteConfirmModal {...deleteModalProps} />
    </div>
  );
}

function DeviceFieldCell({ field, c, ap, showLeftBorder }) {
  const icon = ICON_MAP[field.icon] ?? Ico.device;
  return (
    <div style={{ borderLeft: showLeftBorder ? `1px solid ${ap.cardBorder}` : undefined }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "12px 14px", minWidth: 0 }}>
        <div style={{ width: 26, height: 26, borderRadius: 6, background: ap.fieldIconBg, border: `1px solid ${ap.fieldIconBorder}`, display: "flex", alignItems: "center", justifyContent: "center", color: ap.fieldIconColor, flexShrink: 0 }}>
          {icon}
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 9.5, fontWeight: 700, color: ap.fieldLabel, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4 }}>{field.label}</div>
          <div style={{ fontSize: 13, fontWeight: 700, color: c.text, wordBreak: "break-word", lineHeight: 1.35 }}>{field.value || "—"}</div>
        </div>
      </div>
    </div>
  );
}

function assignBtnStyle(A) {
  return {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    height: 30,
    padding: "0 10px",
    borderRadius: 7,
    border: `1px solid ${A}55`,
    background: `${A}10`,
    color: A,
    fontSize: 12,
    fontWeight: 700,
    cursor: "pointer",
  };
}

function deassignBtnStyle() {
  return {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    height: 30,
    padding: "0 10px",
    borderRadius: 7,
    border: "1px solid #EF444440",
    background: "transparent",
    color: "#EF4444",
    fontSize: 12,
    fontWeight: 700,
    cursor: "pointer",
  };
}

function UnlinkIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
      <line x1="2" y1="2" x2="22" y2="22" />
    </svg>
  );
}
